import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  formatDate,
  parserDateByListFormat,
  DEFAULT_TIME_FORMAT,
  DAYJS_VALID_FORMAT_TIMES,
} from "utils/date";
import { useDeviceSelectors } from "react-device-detect";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Popover } from "react-tiny-popover";
import dayjs from "config/dayjs";
import ja from "date-fns/locale/ja";

// Types and constants
import { Props, DateTimePickerRef, DatePickerModeProps } from "./types";
import { PICKER_MODE } from "./constants";

// Hooks
import { usePickerFormats } from "./hooks/usePickerFormats";
import { useValueFormatter } from "./hooks/useValueFormatter";
import { useInputValidation } from "./hooks/useInputValidation";
import { useMobileTouchHandler } from "./hooks/useMobileTouchHandler";
import { useCleanupOnUnmount } from "./hooks/useCleanupOnUnmount";

// Components
import { PickerIcon } from "./components/PickerIcon";
import { CalendarPopoverContent } from "./components/CalendarPopoverContent";

registerLocale("ja", ja);

// Re-export types and constants for external use
export { PICKER_MODE } from "./constants";
export type { PickerMode } from "./constants";
export type { DateTimePickerRef, Props } from "./types";

const DateTimePicker = forwardRef<DateTimePickerRef, Props>((props, ref) => {
  const {
    mode,
    formatType,
    value,
    placeholderText,
    isReadOnly,
    isLoading,
    inputStyle,
    showIcon = true,
    isShowCalendar,
    isShowTooltip = true,
    clearIconFontSize,
    setIsShowCalendar,
    onChangeStatusValid,
    onChange,
  } = props;

  // State
  const [inputText, setInputText] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState<Date | null>();

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const isComposingRef = useRef(false);
  const lastValidValueRef = useRef<Date | string | null>();
  const isInternalUpdateRef = useRef(false); // Track internal updates to prevent loops
  const isFocusedRef = useRef(false); // Track focus state

  // Device detection
  const [{ isMobile }] = useDeviceSelectors(window.navigator.userAgent);

  // Formats
  const { pickerFormat, dayJsFormat, validationMessage } = usePickerFormats(mode, formatType);

  // Validation
  const { isValid: isValidInput, parsedValue } = useInputValidation(mode, inputText);

  // Format value for display
  const formattedValue = useValueFormatter(mode, value, dayJsFormat);

  // Imperative handle for ref
  useImperativeHandle(ref, () => ({
    focusInput: () => inputRef.current?.focus(),
  }));

  // Initialize selected value and input text from props
  useEffect(() => {
    // Skip if this is an internal update
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;

      return;
    }

    lastValidValueRef.current = value;

    if (!value) {
      setInputText("");
      setSelectedValue(null);

      return;
    }

    // Update input text from formatted value
    setInputText(formattedValue);

    // Update selected value based on mode
    if (mode === PICKER_MODE.DATE) {
      const dateValue = value instanceof Date ? value : new Date(value);
      if (!isNaN(dateValue.getTime())) {
        setSelectedValue(dateValue);
      }
    } else if (mode === PICKER_MODE.TIME && typeof value === "string") {
      const parsed = parserDateByListFormat(value, DAYJS_VALID_FORMAT_TIMES);
      if (parsed) {
        setSelectedValue(parsed);
      }
    }
  }, [value, mode, formattedValue]);

  // Update selected value for time mode when input changes (only for user input)
  useEffect(() => {
    if (mode === PICKER_MODE.TIME && parsedValue && !isInternalUpdateRef.current) {
      setSelectedValue(parsedValue);
    }
  }, [mode, parsedValue]);

  // Notify parent of validation status
  useEffect(() => {
    onChangeStatusValid?.(isValidInput);
  }, [isValidInput, onChangeStatusValid]);

  // ==================== Event Handlers ====================

  // Helper: Handle clearing empty input
  const handleClearInput = useCallback(() => {
    lastValidValueRef.current = null;
    setSelectedValue(null);
    onChange?.(null);
  }, [onChange]);

  // Helper: Handle saving valid value
  const handleSaveValue = useCallback(() => {
    isInternalUpdateRef.current = true;

    if (mode === PICKER_MODE.DATE) {
      const dateValue = selectedValue ?? null;
      lastValidValueRef.current = dateValue;
      (onChange as DatePickerModeProps["onChange"])?.(dateValue);
      
      if (dateValue) {
        setInputText(formatDate(dateValue, dayJsFormat));
      }
    } else {
      // For TIME mode, use parsedValue if selectedValue is not yet set
      const timeDate = selectedValue ?? parsedValue ?? null;
      const timeValue = timeDate
        ? dayjs(timeDate).format(DEFAULT_TIME_FORMAT)
        : "";
      lastValidValueRef.current = timeValue;
      onChange?.(timeValue);
      
      if (timeValue) {
        setInputText(dayjs(timeValue, DEFAULT_TIME_FORMAT).format(dayJsFormat));
      }
    }
  }, [mode, selectedValue, parsedValue, dayJsFormat, onChange]);

  // Helper: Handle resetting invalid input
  const handleResetInput = useCallback(
    (resetValue: Date | string | null | undefined) => {
      isInternalUpdateRef.current = true;
      
      if (!resetValue) {
        setInputText("");
        setSelectedValue(null);

        return;
      }
      
      if (mode === PICKER_MODE.DATE) {
        const dateValue = resetValue instanceof Date ? resetValue : new Date(resetValue);
        if (!isNaN(dateValue.getTime())) {
          setInputText(formatDate(dateValue, dayJsFormat));
          setSelectedValue(dateValue);
        } else {
          setInputText("");
          setSelectedValue(null);
        }
      } else {
        // For TIME mode, resetValue should be a string
        const resetTimeString = typeof resetValue === "string" ? resetValue : "";
        if (resetTimeString) {
          setInputText(dayjs(resetTimeString, DEFAULT_TIME_FORMAT).format(dayJsFormat));
          // Parse and set selectedValue for TIME mode
          const parsed = parserDateByListFormat(resetTimeString, DAYJS_VALID_FORMAT_TIMES);
          setSelectedValue(parsed);
        } else {
          setInputText("");
          setSelectedValue(null);
        }
      }
    },
    [mode, dayJsFormat]
  );

  const handleUpdateValue = useCallback(() => {
    // Clear value when input is empty
    if (!inputText?.trim()) {
      handleClearInput();
    }
    // Save valid value
    else if (isValidInput) {
      handleSaveValue();
    }
    // Reset to last valid value when input is invalid
    else {
      const resetValue = lastValidValueRef.current;
      handleResetInput(resetValue);
    }
  }, [
    inputText,
    isValidInput,
    handleClearInput,
    handleSaveValue,
    handleResetInput,
  ]);

  const onChangeData = useCallback(() => {
    handleUpdateValue();
    inputRef.current?.blur();
    setIsShowCalendar(false);
  }, [
    handleUpdateValue,
    setIsShowCalendar,
  ]);

  const openCalendar = useCallback(() => {
    setTimeout(() => setIsShowCalendar(true), 50);
  }, [setIsShowCalendar]);

  const handleFocus = useCallback(() => {
    if (!isReadOnly) {
      isFocusedRef.current = true;
      openCalendar();
    }
  }, [isReadOnly, openCalendar]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
  }, []);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false;
  }, []);

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Don't process Enter key during IME composition or on mobile
      if (isComposingRef.current || isMobile) {
        return;
      }
      
      if (e.key === "Enter" || e.keyCode === 13) {
        inputRef.current?.blur();
        onChangeData();
      }
    },
    [isMobile, onChangeData]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isReadOnly) return;

      const newValue = e.target.value;
      setInputText(newValue);

      if (mode === PICKER_MODE.DATE) {
        const parsed = parserDateByListFormat(newValue);
        setSelectedValue(parsed);
      }
    },
    [isReadOnly, mode]
  );

  const handleChange = useCallback(
    (d: Date) => {
      if (mode === PICKER_MODE.DATE) {
        const date = formatDate(d, dayJsFormat);
        setInputText(date);
        setSelectedValue(d);
      } else {
        const time = dayjs(d).format(dayJsFormat);
        setInputText(time);
        setSelectedValue(d);
      }
    },
    [mode, dayJsFormat]
  );

  const handleClearClick = useCallback(() => {
    inputRef.current?.focus();
    setInputText("");
    // Trigger update to clear the value properly
    handleClearInput();
  }, [handleClearInput]);

  // Custom hooks for mobile handling - only call onChangeData when focused
  useMobileTouchHandler(
    isMobile,
    useCallback(() => {
      if (isFocusedRef.current) {
        onChangeData();
      }
    }, [onChangeData])
  );
  useCleanupOnUnmount(isShowCalendar, handleUpdateValue);

  // ==================== Render ====================

  return (
    <Box
      height="100%"
      position="relative"
      className="wrapper-input-datepicker"
      display="contents"
    >
      <Popover
        isOpen={isShowCalendar ?? false}
        onClickOutside={onChangeData}
        positions={["top", "bottom", "left"]}
        content={
          <CalendarPopoverContent
            mode={mode}
            selectedValue={selectedValue}
            pickerFormat={pickerFormat}
            inputRef={inputRef}
            onChange={handleChange}
          />
        }
      >
        <InputGroup zIndex={1000}>
          {showIcon && (
            <InputLeftElement
              pointerEvents="none"
              w="3.2rem"
              className="wrapper-left-icon-date"
              h="100%"
              fontSize="1.4rem"
            >
              <PickerIcon mode={mode} />
            </InputLeftElement>
          )}

          <Input
            ref={inputRef}
            isReadOnly={isReadOnly}
            backgroundColor="white"
            className="input-form-date"
            height="4rem"
            maxWidth="100%"
            width="100%"
            placeholder={placeholderText}
            fontSize="1.6rem"
            value={inputText}
            onKeyUp={handleKeyUp}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            color="#171717"
            _readOnly={{ cursor: "pointer" }}
            border={`1px solid ${isValidInput ? "#D4D4D4" : "crimson"}`}
            boxShadow={!isValidInput ? "0 0 1px 1px crimson !important" : undefined}
            borderColor={!isValidInput ? "crimson !important" : undefined}
            paddingLeft="3rem"
            paddingRight="3rem"
            style={inputStyle}
          />

          {!isReadOnly && inputText && (
            <InputRightElement
              cursor="pointer"
              className="wrapper-clear-form-date"
              tabIndex={0}
              onClick={handleClearClick}
              fontSize="1.4rem"
              height="100%"
            >
              <CloseIcon
                className="icon-clear-date"
                color="#171717"
                fontSize={clearIconFontSize ?? "0.9rem"}
                zIndex={10}
              />
            </InputRightElement>
          )}
        </InputGroup>
      </Popover>

      {!isValidInput && isShowTooltip && (
        <Text className="tooltip-text">{validationMessage}</Text>
      )}

      {isLoading && (
        <Flex
          justifyContent="center"
          alignItems="center"
          position="absolute"
          right={0}
          top={0}
          zIndex={10001}
          w="3rem"
        >
          <Spinner />
        </Flex>
      )}
    </Box>
  );
});

DateTimePicker.displayName = "DateTimePicker";

export default DateTimePicker;
